import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { signupTrackingFields } from '@/utils/signupTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Background image URL from user's uploaded asset
const RIPPLE_BG = 'https://daily-obx-cdn.b-cdn.net/newsletter-images/2026-OBXBB-2022-07-20-8043-Edit-Edit-Edit-Edit.jpg';

function Subscribe() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [loading, setLoading] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot — real visitors never see/fill this field

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/subscribers`, {
        email, frequency, website, ...signupTrackingFields('subscribe_page'),
      });
      if (response.data.message === 'Frequency updated') {
        // Already following — they just switched between daily and weekly.
        toast.success(response.data.frequency === 'daily'
          ? "You're already following — you'll now get every new story."
          : "You're already following — you'll now get the weekly roundup.");
      } else if (response.data.message || response.status === 200) {
        navigate('/subscribe-success');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Common full-screen background style
  const pageStyle = {
    minHeight: '100vh',
    backgroundImage: `url(${RIPPLE_BG})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  };

  // White card style
  const cardStyle = {
    background: 'rgba(255, 255, 255, 0.97)',
    borderRadius: '8px',
    padding: '45px 40px',
    maxWidth: '420px',
    width: '100%',
    boxShadow: '0 4px 30px rgba(0,0,0,0.15)'
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle} data-testid="subscribe-step-1">
        <div style={{ textAlign: 'center' }}>
          {/* Logo */}
          <img
            src="https://daily-obx-cdn.b-cdn.net/newsletter-images/theobxbeachbumlogo.png"
            alt="the OBX Beach Bum"
            style={{ width: '100%', maxWidth: '340px', height: 'auto', marginBottom: '24px' }}
          />

          {/* Headline */}
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: '22px',
            fontWeight: '700',
            margin: '0 0 20px',
            color: '#1a1a1a',
            lineHeight: '1.4'
          }}>
            I finally have a new way to share<br />my Outer Banks photography.
          </h1>

          {/* Subtext */}
          <p style={{
            fontSize: '15px',
            color: '#555',
            margin: '0 0 8px',
            lineHeight: '1.6'
          }}>
            No algorithms deciding what you see.
          </p>
          <p style={{
            fontSize: '15px',
            color: '#555',
            margin: '0 0 8px',
            lineHeight: '1.6'
          }}>
            Just my mostly good photography of the Outer Banks,<br />and the occasional thing worth saying.
          </p>
          <p style={{
            fontSize: '15px',
            color: '#555',
            margin: '0 0 35px',
            lineHeight: '1.6'
          }}>
            Straight to your inbox, free.
          </p>

          {/* Email Form */}
          <form onSubmit={handleEmailSubmit}>
            {/* Honeypot — hidden from real visitors, bots that auto-fill every field trip it */}
            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
            />
            <div style={{ textAlign: 'left', marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#1a1a1a',
                marginBottom: '8px'
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  fontSize: '16px',
                  border: '1px solid #d0d0d0',
                  borderRadius: '4px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                data-testid="subscribe-email-input"
              />
            </div>

            <div style={{ textAlign: 'left', marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#1a1a1a',
                marginBottom: '8px'
              }}>
                How often?
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  padding: '12px 14px', border: `1px solid ${frequency === 'daily' ? '#1a1a1a' : '#d0d0d0'}`,
                  borderRadius: '4px', cursor: 'pointer',
                  background: frequency === 'daily' ? '#f8f8f8' : '#fff'
                }}>
                  <input type="radio" name="frequency" value="daily" checked={frequency === 'daily'}
                    onChange={(e) => setFrequency(e.target.value)} style={{ marginTop: '3px' }}
                    data-testid="subscribe-frequency-daily" />
                  <span style={{ fontSize: '14px', color: '#333' }}>
                    <strong>As it happens</strong> — an email each time I publish
                  </span>
                </label>
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  padding: '12px 14px', border: `1px solid ${frequency === 'weekly' ? '#1a1a1a' : '#d0d0d0'}`,
                  borderRadius: '4px', cursor: 'pointer',
                  background: frequency === 'weekly' ? '#f8f8f8' : '#fff'
                }}>
                  <input type="radio" name="frequency" value="weekly" checked={frequency === 'weekly'}
                    onChange={(e) => setFrequency(e.target.value)} style={{ marginTop: '3px' }}
                    data-testid="subscribe-frequency-weekly" />
                  <span style={{ fontSize: '14px', color: '#333' }}>
                    <strong>Weekly digest</strong> — one roundup email each Monday
                  </span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '16px',
                fontWeight: '600',
                background: '#1a1a1a',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'background 0.2s'
              }}
              data-testid="subscribe-continue-btn"
            >
              {loading ? 'Following...' : 'Follow'}
            </button>
          </form>

          {/* Footer text */}
          <p style={{
            fontSize: '13px',
            color: '#888',
            margin: '20px 0 0',
            textDecoration: 'underline',
            textDecorationColor: '#ccc'
          }}>
            Free newsletter. Unsubscribe anytime.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Subscribe;
